import { memo, useCallback, useMemo, useRef, ReactElement } from 'react';
import { PlusCircleOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import {
  ActionEntityType,
  GroupedExchangeRateResponse,
  ExchangeRateCrudRequest,
  RateEntry,
  SupportedCurrency,
  TargetCurrency,
} from '@pmp/api';
import { Guard, useGuard } from '@ohpen/react-authz';
import { useExportTemplatesStore } from '@pmp/stores';

import { exchangeRateColumns, historicColumns } from '../tableConfig';
import { ExchangeRatesListProps, ExchangeRateDelete } from '../interfaces';

const entityType = ActionEntityType.EXCHANGE_RATES;
const rowKey = 'sourceCurrency';

export const ExchangeRatesList = memo(
  ({
    isFetching,
    valueLists,
    data,
    onCreate,
    onView,
    onCreateNewExchangeRate,
    onDeleteExchangeRate,
  }: ExchangeRatesListProps): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();
    const { allowed: showRowSelection } = useGuard({ flags: ['enableExports'] });
    const { t } = useTranslation();

    const getTemplates = useCallback(() => getExportTemplates(ActionEntityType.EXCHANGE_RATES), [getExportTemplates]);

    const getRowActions: (record: GroupedExchangeRateResponse) => ReactElement[] = useCallback(
      record => {
        const addNewExchangeRateTitle = record.lockedByActionId ? (
          <>
            {t('global.buttons.addLockedByChangeRequest')}
            <Link to={`/change-requests?search-change-requests=${encodeURIComponent(record.lockedByActionId)}`}>
              {record.lockedByActionId}
            </Link>
          </>
        ) : (
          t('pmp.exchangeRates.labels.addExchangeRate')
        );

        const newRate: ExchangeRateCrudRequest = {
          sourceCurrency: record.sourceCurrency as SupportedCurrency,
          targetCurrency: record.targetCurrency as TargetCurrency,
          rate: null,
          startDate: null,
        };

        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button
              type="text"
              aria-label="View exchange rate"
              icon={<EyeOutlined />}
              onClick={() => onView(record as ExchangeRateCrudRequest)}
            />
          </Tooltip>,
          <Guard key="add" permissions={['exchangeRates:update']} flags={['enableExchangeRates']}>
            <Tooltip key="add" title={addNewExchangeRateTitle} placement="top">
              <Button
                type="text"
                aria-label="Add new exchange rate"
                icon={<PlusCircleOutlined key="create" />}
                onClick={() => onCreateNewExchangeRate(newRate)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
        ];

        return rowActions;
      },
      [onView, onCreateNewExchangeRate, t],
    );

    const getExpandedRowActions: (record: RateEntry, parentRecord: GroupedExchangeRateResponse) => ReactElement[] = useCallback(
      (record, parentRecord) => {
        const data: ExchangeRateDelete = {
          id: `${parentRecord.sourceCurrency}#${record.startDate}`,
          name: `${parentRecord.sourceCurrency} - ${record.rate} - ${record.startDate}`,
        };

        return [
          <Guard key="delete" permissions={['exchangeRates:delete']} flags={['enableExchangeRates']}>
            <Tooltip key="delete" title={t('global.labels.delete')} placement="top">
              <Button
                type="text"
                aria-label="Delete exchange rate"
                icon={<DeleteOutlined key="delete" />}
                onClick={() => onDeleteExchangeRate(data)}
                disabled={dayjs() > dayjs(record.startDate)}
              />
            </Tooltip>
          </Guard>,
        ];
      },
      [onDeleteExchangeRate, t],
    );

    const tableProps: TableProps<ActionEntityType, GroupedExchangeRateResponse, any, unknown> = useMemo(() => {
      return {
        id: entityType,
        ref: tableRef,
        dataSource: data ?? [],
        columns: exchangeRateColumns,
        rowKey,
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        valueLists,
        getRowActions,
        loading: isFetching,
        expanded: {
          columns: historicColumns,
          columnsKey: 'rates',
          rowKey: 'index',
          getRowActions: getExpandedRowActions,
        },
        showRowSelection,
        showViews: true,
      };
    }, [data, valueLists, getRowActions, isFetching, getExpandedRowActions, showRowSelection]);

    return (
      <Table {...tableProps}>
        <>
          <Table.ActionsBar>
            <Flex justify="space-between">
              <Space size="middle">
                <Table.GlobalSearch />
              </Space>
              <Space size="middle">
                <Table.ResetFiltersButton />
                <Table.SelectColumnsButton />
                <Guard flags={['enableExports']}>
                  <Table.ExportButton
                    editableFileName
                    fileName={`Exchange rates ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                    sheetName="Exchange rates"
                    disabled={isFetching}
                    loadingTemplates={isLoadingExportTemplates}
                    getTemplates={getTemplates}
                    modalDefaults={{ columnScope: 'template' }}
                    allowSearch={true}
                    exportView
                    flatten
                  />
                </Guard>
                <Guard permissions={['exchangeRates:create']} flags={['enableExchangeRates']}>
                  <Table.CreateButton onClick={onCreate} />
                </Guard>
              </Space>
            </Flex>
          </Table.ActionsBar>
          <Table.BulkBar xItemsSelectedLabel="pmp.exchangeRates.labels.xExchangeRatesSelected">
            <Space size="middle">
              <Guard flags={['enableExports']}>
                <Table.ExportButton
                  editableFileName
                  fileName={`Exchange rates ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                  sheetName="Exchange rates"
                  disabled={isFetching}
                  loadingTemplates={isLoadingExportTemplates}
                  allowSearch={true}
                  getTemplates={getTemplates}
                  modalDefaults={{ columnScope: 'template' }}
                  exportView
                  flatten
                />
              </Guard>
            </Space>
          </Table.BulkBar>
        </>
      </Table>
    );
  },
);
